import type { SeriesView } from '@aj/core';
import { Button, Card, HStack, Sparkline, Text, VStack } from '@aj/ui';
import { useState } from 'react';

import { ReadingForm } from '../../components/ReadingForm.js';
import { dateToMs } from '../../lib/dates.js';
import { useI18n, useT } from '../../providers/I18nProvider.js';

/**
 * Results, and how they are moving (§53.7).
 *
 * The only place in the product allowed to say a student is getting better at
 * something — and therefore the only place that can say the opposite. Two
 * rules keep that honest:
 *
 *  • **A fall is never red.** Direction is carried by a sign and by words, the
 *    same as the missed cell in the grid. The record is a record, not a
 *    scolding, and a student opening this after a bad test must not be met
 *    with the colour of failure.
 *  • **A count never claims anything.** Pages read and kilograms have no good
 *    direction, so nothing is asserted about them — `assertsSkill` decides,
 *    and it is computed in core rather than trusted to this file.
 */
export type SeriesCardProps = { series: SeriesView[] };

export function SeriesCard({ series }: SeriesCardProps) {
  const t = useT();
  const [adding, setAdding] = useState(false);

  return (
    <Card padding={5}>
      <VStack gap={4}>
        <HStack gap={3} align="baseline" justify="between">
          <Text variant="overline" tone="subtle">
            {t('measure.title')}
          </Text>
        </HStack>

        {series.length === 0 && !adding ? (
          <VStack gap={2}>
            <Text variant="label" tone="ink">
              {t('measure.empty.title')}
            </Text>
            <Text variant="body" tone="muted">
              {t('measure.empty.body')}
            </Text>
          </VStack>
        ) : (
          <VStack as="ul" gap={3} aria-label={t('measure.title')}>
            {series.map((item) => (
              <Row key={item.id} series={item} />
            ))}
          </VStack>
        )}

        {adding ? (
          <ReadingForm onDone={() => setAdding(false)} />
        ) : (
          <Button variant="secondary" icon="plus" onClick={() => setAdding(true)} className="self-start">
            {t('measure.add')}
          </Button>
        )}
      </VStack>
    </Card>
  );
}

function Row({ series }: { series: SeriesView }) {
  const t = useT();
  const i18n = useI18n();
  const { latest, shape } = series;

  const value =
    shape === 'count'
      ? `${i18n.formatNumber(latest.value)}${series.unit ? ` ${series.unit}` : ''}`
      : t(shape === 'rank' ? 'measure.valueRank' : 'measure.valueOutOf', {
          value: i18n.formatNumber(latest.value),
          outOf: i18n.formatNumber(latest.outOf ?? 0),
        });

  return (
    <HStack as="li" gap={3} align="center" justify="between" className="flex-wrap">
      <VStack gap={0} className="min-w-0 flex-1">
        <Text variant="label" tone="ink" className="truncate">
          {series.label}
        </Text>
        <Text variant="caption" tone="subtle" className="truncate">
          {deltaLabel(series, t, i18n)}
        </Text>
      </VStack>

      <HStack gap={3} align="center" className="shrink-0">
        <Text variant="body" tone="ink" numeric>
          {value}
        </Text>
        <Sparkline
          label={t('measure.trendLabel', { name: series.label })}
          points={series.points.map((point) => ({
            plot: point.plot,
            label: t('measure.readingOn', {
              value: i18n.formatNumber(point.value),
              date: i18n.formatDate(dateToMs(point.date), { day: 'numeric', month: 'short' }),
            }),
          }))}
        />
      </HStack>
    </HStack>
  );
}

/**
 * The change, in the units a reader expects, with the sign spelled out.
 *
 * A rank reads as places gained because that is how a student says it: "I went
 * up 28 places", never "my rank decreased by 28".
 */
function deltaLabel(
  series: SeriesView,
  t: ReturnType<typeof useT>,
  i18n: ReturnType<typeof useI18n>,
): string {
  const { delta, shape } = series;
  if (delta === null) return t('measure.deltaFirst');
  if (delta === 0) return t('measure.deltaSame');

  const sign = delta > 0 ? '+' : '−';
  const size = Math.abs(delta);

  if (shape === 'outOf') return `${sign}${t('measure.deltaPoints', { count: size })}`;
  if (shape === 'rank') return `${sign}${t('measure.deltaPlaces', { count: size })}`;
  return `${sign}${i18n.formatNumber(size)}${series.unit ? ` ${series.unit}` : ''}`;
}
